import { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { LogoMark, ShieldIcon } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import { NotAdminError, useAuthStore } from '@/store/auth'

/**
 * The administrators' door. Same accounts as the job seeker app; only one
 * whose role is `admin` gets through. There is no "create account" here: an
 * administrator is granted from the server (`npm run admin -- grant <email>`).
 */
export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const status = useAuthStore((state) => state.status)
  const login = useAuthStore((state) => state.login)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [show, setShow] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const from = (location.state as { from?: string } | null)?.from ?? '/'
  if (status === 'authenticated') return <Navigate to={from} replace />

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await login(email.trim(), password)
      navigate(from, { replace: true })
    } catch (caught) {
      if (caught instanceof NotAdminError) setError('That account is not an administrator. Ask whoever runs the server to grant it.')
      else if (caught instanceof ApiError) setError(caught.status === 401 ? 'That email and password do not match.' : caught.message)
      else setError('Could not reach the server. Check that the API is running.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="relative flex min-h-dvh flex-col items-center justify-center overflow-hidden bg-canvas px-4 py-10">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(rgb(var(--ink) / 0.045) 1px, transparent 1px), linear-gradient(90deg, rgb(var(--ink) / 0.045) 1px, transparent 1px)',
          backgroundSize: '36px 36px',
          maskImage: 'radial-gradient(ellipse 55% 50% at 50% 45%, #000 20%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 55% 50% at 50% 45%, #000 20%, transparent 100%)',
        }}
      />
      <div className="relative w-full max-w-[400px] animate-fade-up">
        <div className="mb-7 flex items-center gap-3">
          <LogoMark className="h-10 w-10" />
          <div>
            <p className="text-[18px] font-semibold tracking-tight text-ink">JobSlide</p>
            <p className="label-eyebrow mt-1">Admin portal</p>
          </div>
        </div>

        <form onSubmit={submit} className="card space-y-4 p-5 sm:p-6" noValidate>
          <div>
            <h1 className="text-[20px] font-semibold tracking-[-0.01em] text-ink">Sign in</h1>
            <p className="mt-1 text-[13.5px] text-ink-muted">For administrators. Use your JobSlide account.</p>
          </div>
          <TextField appearance="box" label="Email" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required autoFocus />
          <TextField
            appearance="box"
            className="pr-16"
            label="Password"
            type={show ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            trailing={
              <button type="button" onClick={() => setShow((value) => !value)} className="px-3.5 text-[12.5px] font-medium text-ink-muted hover:text-ink">
                {show ? 'Hide' : 'Show'}
              </button>
            }
          />
          {error && (
            <p role="alert" className="rounded-field bg-danger/[0.08] px-3.5 py-2.5 text-[13px] leading-relaxed text-danger">
              {error}
            </p>
          )}
          <Button type="submit" fullWidth loading={busy} disabled={!email.trim() || !password}>
            Sign in
          </Button>
        </form>

        <p className="mt-5 flex items-start gap-2 px-1 text-[12px] leading-relaxed text-ink-faint">
          <ShieldIcon className="mt-px h-4 w-4 shrink-0" />
          What you do for a user here is recorded on that user's timeline.
        </p>
      </div>
    </div>
  )
}
